import React from 'react';
import { useDemo, DemoRole } from '../context/DemoContext';
import { useTenant } from '../context/TenantContext';
import './Demo.css';

const ROLE_LABEL: Record<DemoRole, string> = {
  student: 'Explore as a student',
  instructor: 'Explore as an instructor',
  owner: 'Explore as the studio owner',
};

const ROLE_ICON: Record<DemoRole, string> = {
  student: 'M12 3 1 9l11 6 9-4.91V17h2V9L12 3zM5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82z',
  instructor: 'M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z',
  owner: 'M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z',
};

/** Landing screen for the public demo studio (logged-out visitors). */
const DemoWelcome: React.FC = () => {
  const { accounts, entering, error, enterAs } = useDemo();
  const tenant = useTenant();
  if (!accounts) return null;

  // Student first: it's the experience studios are buying for their members.
  const order: DemoRole[] = ['student', 'instructor', 'owner'];
  const sorted = order
    .map((r) => accounts.find((a) => a.role === r))
    .filter((a): a is NonNullable<typeof a> => !!a);

  return (
    <section className="demo-welcome" aria-labelledby="demo-welcome-title">
      <span className="demo-eyebrow">Live demo · LearnSalsa for studios</span>
      <h1 id="demo-welcome-title">
        See your studio on LearnSalsa from every seat.
      </h1>
      <p className="demo-lede">
        This is {tenant?.name || 'a sample studio'}, set up the way yours would be. Students
        practice between classes with slow-motion, mirrored, looping video, and instructors coach them
        remotely. Pick a seat and look around. Nothing you do here is permanent.
      </p>

      <div className="demo-roles">
        {sorted.map((a) => (
          <button
            key={a.role}
            type="button"
            className="demo-role-card"
            onClick={() => enterAs(a.role)}
            disabled={entering !== null}
            aria-busy={entering === a.role}
          >
            <span className="demo-role-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d={ROLE_ICON[a.role]} /></svg>
            </span>
            <span className="demo-role-title">{ROLE_LABEL[a.role]}</span>
            <span className="demo-role-who">as {a.name}</span>
            <span className="demo-role-blurb">{a.blurb}</span>
            <span className="demo-role-cta">{entering === a.role ? 'Signing in…' : 'Start →'}</span>
          </button>
        ))}
      </div>

      {error && <p className="demo-error" role="alert">{error}</p>}
      <p className="demo-footnote">
        No sign-up needed. The demo resets every night. The dancers, videos and lessons here are
        AI-generated samples, not real instruction, and haven't been checked for technique. Your studio
        would use your own videos.
      </p>
    </section>
  );
};

export default DemoWelcome;
